<template>
  <div>
    <rest-list :payload="fetchEndpoint" ref="list">
      <template>
        <rest-list-column
          v-if="selectedColumns.name"
          prop="name"
          :title="$t('Metabase_MetabaseComputation_Field_Name')"
          :min-width="200"
          :sortable="true"
        >
          <template slot-scope="scope">
            <a
              href="#"
              @click="view(scope.row)"
              onclick="event.preventDefault()"
            >
              {{ scope.row.name }}
            </a>
          </template>
        </rest-list-column>

        <rest-list-column
          v-if="selectedColumns.profileAttributes"
          prop="attributes"
          :title="$t('Metabase_MetabaseComputation_Field_ProfileAttributes')"
          :min-width="200"
          :sortable="false"
        >
          <template slot-scope="scope">
            <el-tooltip
              :open-delay="600"
              :content="scope.row.attributes.join(', ')"
              placement="top"
            >
              <p>
                {{ scope.row.attributes.join(', ') }}
              </p>
            </el-tooltip>
          </template>
        </rest-list-column>

        <rest-list-column
          v-if="selectedColumns.description"
          prop="description"
          :title="$t('Metabase_MetabaseComputation_Field_Description')"
          :min-width="200"
          :sortable="false"
        />

        <rest-list-column-enum
          v-if="selectedColumns.status"
          prop="status"
          :title="$t('Metabase_MetabaseComputation_Field_Status')"
          :enum="MetabaseComputationStatusEnum"
          :min-width="100"
          :sortable="false"
        >
          <template slot-scope="scope">
            {{ $t(MetabaseComputationStatusEnum[scope.row.status]) }}
          </template>
        </rest-list-column-enum>

        <rest-list-column-enum
          v-if="selectedColumns.frequency"
          prop="frequency"
          :title="$t('Metabase_MetabaseComputation_Field_Frequency')"
          :enum="MetabaseComputationFrequencyEnum"
          :min-width="100"
          :sortable="false"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.frequency">
              {{ $t(MetabaseComputationFrequencyEnum[scope.row.frequency]) }}
            </template>
            <template v-else>
              -
            </template>
          </template>
        </rest-list-column-enum>

        <rest-list-column
          v-if="selectedColumns.lastSynced"
          prop="lastRun"
          :title="$t('Metabase_MetabaseComputation_Field_LastComputed')"
          :filterable="false"
          :sortable="false"
          :filter-input="false"
          :width="200"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.lastRun">
              <router-link :to="taskRoute(scope.row.lastRun)" class="taskLink">
                <el-link
                  :type="taskStatusDetails(scope.row.lastRun.status).type"
                  :icon="taskStatusDetails(scope.row.lastRun.status).icon"
                  :underline="false"
                />
                {{ dateFormatter(scope.row.lastRun.create_time) }}
              </router-link>
            </template>
            <template v-else>
              -
            </template>
          </template>
        </rest-list-column>

        <rest-list-column-actions>
          <template slot="header">
            <rest-list-column-settings
              storage-key="metabase-computations-columns"
              :title="$t('Metabase_MetabaseComputation_Field_Settings')"
              :columns="columns"
              v-model="selectedColumns"
            />
          </template>
          <template slot-scope="scope">
            <rest-list-action
              :title="$t('Metabase_MetabaseComputation_Field_Preview')"
              icon="el-icon-view"
              :click="view"
              :row="scope.row"
            />

            <rest-list-action
              v-if="metabaseToken"
              :title="$t('Metabase_MetabaseComputation_Field_Settings')"
              icon="el-icon-setting"
              :click="settings"
              :row="scope.row"
            />

            <rest-list-action
              v-if="
                scope.row.status === 'unverified' &&
                  $can('MetabaseComputation:FullAccess')
              "
              :title="$t('Metabase_MetabaseComputation_Field_Activate')"
              icon="el-icon-circle-check"
              :click="validate"
              :row="scope.row"
            />

            <rest-list-action
              v-if="
                scope.row.status === 'verified' &&
                  $can('MetabaseComputation:FullAccess')
              "
              :title="$t('Metabase_MetabaseComputation_Field_Deactivate')"
              icon="el-icon-circle-close"
              :click="deactivate"
              :row="scope.row"
            />

            <rest-list-action
              v-if="
                scope.row.status === 'verified' &&
                  scope.row.frequency !== 'realtime' &&
                  $can('MetabaseComputation:FullAccess')
              "
              :title="$t('Metabase_MetabaseComputation_Field_Compute')"
              icon="el-icon-refresh"
              :click="compute"
              :row="scope.row"
            />
          </template>
        </rest-list-column-actions>
      </template>
    </rest-list>

    <metabase-computation-dialog
      v-if="validateDialogVisible"
      :data="validateDialogData"
      :readonly="validateDialogReadonly"
      @close="closeDialog"
      @confirm="activate"
    />
  </div>
</template>

<script>
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListAction from '@/common/components/rest/RestListAction'
import { MetabaseComputationStatusEnum } from '@/modules/metabase/enum/MetabaseComputationStatusEnum'
import { MetabaseComputationFrequencyEnum } from '@/modules/metabase/enum/MetabaseComputationFrequencyEnum'
import {
  projectEndpoint,
  projectRoute,
  projectTimezoneDate
} from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import MetabaseComputationDialog from '@/modules/metabase/components/MetabaseComputationDialog'
import RestList from '@/common/components/rest/RestList'
import {
  taskStatusEnum,
  taskStatusLabel
} from '@/modules/tasks/enum/TaskRecordEnums'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'

export default {
  name: 'MetabaseComputationListAll',

  components: {
    RestListColumnSettings,
    RestListColumnEnum,
    RestList,
    MetabaseComputationDialog,
    RestListAction,
    RestListColumnActions,
    RestListColumn
  },

  data() {
    return {
      MetabaseComputationStatusEnum,
      MetabaseComputationFrequencyEnum,
      taskStatusLabel,
      taskStatusEnum,
      validateDialogId: null,
      validateDialogData: null,
      validateDialogVisible: false,
      validateDialogReadonly: null,
      selectedColumns: {}
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: 'metabase-computations'
      })
    },

    metabaseToken() {
      return window.xp.metabaseToken
    },

    columns() {
      return {
        name: {
          title: this.$t('Metabase_MetabaseComputation_Field_Name'),
          defaultValue: true,
          validation: true
        },
        profileAttributes: {
          title: this.$t(
            'Metabase_MetabaseComputation_Field_ProfileAttributes'
          ),
          defaultValue: true,
          validation: true
        },
        description: {
          title: this.$t('Metabase_MetabaseComputation_Field_Description'),
          defaultValue: true,
          validation: true
        },
        status: {
          title: this.$t('Metabase_MetabaseComputation_Field_Status'),
          defaultValue: true,
          validation: true
        },
        frequency: {
          title: this.$t('Metabase_MetabaseComputation_Field_Frequency'),
          defaultValue: true,
          validation: true
        },
        lastSynced: {
          title: this.$t('Metabase_MetabaseComputation_Field_LastComputed'),
          defaultValue: true,
          validation: true
        }
      }
    }
  },

  methods: {
    view(row) {
      this.validate(row, true)
    },

    settings(row) {
      this.$router.push(
        projectRoute({
          name: 'MetabaseProjectApplication',
          query: {
            path: '/question/' + row.id
          }
        })
      )
    },

    validate(row, readonly) {
      RestApi(
        projectEndpoint({
          method: 'post',
          url: 'metabase-computations/' + row.id + '/validate'
        })
      )
        .then(({ data }) => {
          this.validateDialogData = data
          this.validateDialogVisible = true
          this.validateDialogReadonly = readonly
          this.validateDialogId = row.id
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    activate(params) {
      RestApi(
        projectEndpoint({
          method: 'post',
          url: 'metabase-computations/' + this.validateDialogId + '/activate',
          data: params
        })
      )
        .then(() => {
          this.closeDialog()
          this.refresh()

          this.$message({
            message: this.$t('Metabase_MetabaseComputation_Activate_Message', {
              frequency: params.frequency
            }),
            type: 'success'
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    deactivate(row) {
      this.$msgbox({
        title: this.$t('Metabase_MetabaseComputation_DisableSync_Title'),
        message: this.$t('Metabase_MetabaseComputation_DisableSync_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t(
          'Metabase_MetabaseComputation_DisableSync_Confirm'
        ),
        cancelButtonText: this.$t(
          'Metabase_MetabaseComputation_DisableSync_Cancel'
        ),
        dangerouslyUseHTMLString: true,
        center: true
      }).then(() => {
        RestApi(
          projectEndpoint({
            method: 'post',
            url: 'metabase-computations/' + row.id + '/deactivate'
          })
        )
          .then(() => {
            this.closeDialog()
            this.refresh()

            this.$message({
              message: this.$t(
                'Metabase_MetabaseComputation_Deactivate_Message'
              ),
              type: 'success'
            })
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      })
    },

    compute(row) {
      RestApi(
        projectEndpoint({
          method: 'post',
          url: 'metabase-computations/' + row.id + '/compute'
        })
      )
        .then(({ data }) => {
          this.$router.push(
            projectRoute({
              name: 'TaskHistoryView',
              query: { id: data.id }
            })
          )
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    closeDialog() {
      this.validateDialogData = null
      this.validateDialogVisible = false
      this.validateDialogId = null
    },

    refresh() {
      this.$refs['list'].refresh()
    },

    taskStatusDetails(status) {
      if (
        status === this.taskStatusEnum.NEW ||
        status === this.taskStatusEnum.EXECUTING
      ) {
        return {
          icon: 'el-icon-loading',
          type: 'warning',
          message: this.$t('TaskHistory_StatusLoading')
        }
      } else if (
        status === this.taskStatusEnum.ERROR ||
        status === this.taskStatusEnum.FATAL
      ) {
        return {
          icon: 'el-icon-error',
          type: 'danger',
          message: this.$t(this.taskStatusLabel[status])
        }
      } else {
        return {
          icon: 'el-icon-success',
          type: 'success',
          message: this.$t(this.taskStatusLabel[status])
        }
      }
    },

    taskRoute(task) {
      return projectRoute({
        name: 'TaskHistoryView',
        query: { id: task.id }
      })
    },

    dateFormatter(value) {
      return projectTimezoneDate(value).format('DD MMM YYYY HH:mm:ss')
    }
  }
}
</script>

<style scoped>
.taskLink {
  text-decoration: none !important;
}
.taskLink .el-link {
  text-decoration: none !important;
  margin-right: 3px;
}
</style>



// WEBPACK FOOTER //
// src/modules/metabase/pages/MetabaseComputationListAll.vue